import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { Trans } from 'react-i18next';
import { Loading } from 'components/uikit/suspense/Loading';
import { QueryFactory } from 'services/api';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { Input } from 'components/uikit/inputs/Input';

import styles from './DeleteSignerModal.module.scss';
import { Field } from 'components/uikit/Field';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import { useSignatureBlockQueriesInvalidation } from 'pages/authorized/documents/documentList/signatureBlock/useSignatureBlockQueriesInvalidation';
import { showErrorToast } from 'components/uikit/toast/showToast';

type DeleteSignerModalProps = {
  isOpen: boolean;
  signerId: number;
  onClose: () => void;
};

export const DeleteSignerModal: FC<DeleteSignerModalProps> = (props) => {
  const i18n = useScopedTranslation('Component.DeleteSignerModal');
  const buttonTitle = useScopedTranslation('Button.Title');
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [confirmSignerName, setConfirmSignerName] = useState(String);
  const invalidateSignatureBlockQueries = useSignatureBlockQueriesInvalidation(
    {},
  );

  const signerQuery = QueryFactory.SignerQuery.useGetQuery(
    { id: props.signerId },
    {
      enabled: !!props.signerId,
    },
  );
  const projectNamesQuery = QueryFactory.SignerQuery.useVerifyPatchAbilityQuery(
    { id: props.signerId },
    {
      enabled: !!props.signerId,
    },
  );

  const signerFullName = `${signerQuery.data?.firstName} ${signerQuery.data?.lastName}`;
  const isDeleteDisabled = confirmSignerName !== signerFullName;

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      isBlocking={true}
      title={i18n.t('title')}
    >
      <Loading loading={loading} testId={'delete-signer-confirmation-modal'}>
        <div className={styles.message}>
          <Trans
            i18nKey={'Component.DeleteSignerModal.message'}
            values={{
              name: signerFullName,
              projects: projectNamesQuery.data?.join(', '),
            }}
            components={{
              red: <span className={styles.danger} />,
              pre: <span className={styles.noWhiteSpace} />,
              p: <p />,
            }}
          />
          <Field
            className={styles.confirmField}
            title={i18n.t('confirm_field')}
          >
            <Input
              value={confirmSignerName}
              onChange={(e) => {
                setConfirmSignerName(e.target.value);
              }}
            ></Input>
          </Field>
        </div>
        <div className={styles.footer}>
          <Button
            title={buttonTitle.t('cancel')}
            color={ButtonColor.Secondary}
            className={styles.button}
            onClick={async () => {
              props.onClose();
            }}
          />
          <Button
            testId={'delete-button'}
            title={buttonTitle.t('delete')}
            color={ButtonColor.DestroyPrimary}
            className={styles.button}
            disabled={isDeleteDisabled}
            onClick={async () => {
              try {
                setLoading(true);
                await QueryFactory.SignerClient.delete_(props.signerId);
                await queryClient.invalidateQueries({
                  queryKey: QueryFactory.SignerQuery.searchQueryKey(),
                });
                await invalidateSignatureBlockQueries();
                props.onClose();
              } catch (e) {
                showErrorToast((e as Error).message);
              } finally {
                setLoading(false);
              }
            }}
          />
        </div>
      </Loading>
    </CustomModal>
  );
};
